import { AssistantProcess, Device } from "@models";
import { TemplateConfig, TemplateQuestion } from "@types";
import { getElementConfigByDataKey } from "@utils";
import { ContextState } from "src/components/Form/utils/suggestion";

/**
 * Configuration interface for data key config prompt building
 */
export interface DataKeyConfig {
  dataKeyId: string;
  question: string;
  hintText?: string;
  outputFormat?: string;
  additionalFormatInstructions?: Record<string, any>;
  exampleAnswer?: string;
}

/**
 * Options for building the prompt
 */
export interface PromptBuilderOptions {
  includeContext?: boolean;
  includeDeviceInfo?: boolean;
  customInstructions?: string;
}

/**
 * Builds a structured prompt based on data key configuration
 * @param config - The data key configuration
 * @param options - Additional options for prompt building
 * @returns A formatted prompt string
 */
export function buildPrompt({
  instructions,
  //contextConfig,
  device,
  additionalContext,
}: {
  instructions: string;
  currentDataKeyConfig?: DataKeyConfig;
  contextConfig?: ContextState;
  device?: Device;
  additionalContext?: string;
}): string {
  const sections: string[] = [];

  sections.push("<context>");
  if (device) {
    sections.push(`<device-info>`);
    sections.push(`${JSON.stringify(device)}`);
    sections.push(`</device-info>`);
    sections.push("");
  }

  // if (currentDataKeyConfig) {
  //   sections.push("<data-key-config>");

  //   // Add data key ID
  //   sections.push(`<data-key-id>`);
  //   sections.push(`${currentDataKeyConfig.dataKeyId}`);
  //   sections.push(`</data-key-id>`);
  //   sections.push("");

  //   // Add question
  //   sections.push(`<question>`);
  //   sections.push(`${currentDataKeyConfig.question}`);
  //   sections.push(`</question>`);
  //   sections.push("");

  //   // Add hint text if provided
  //   if (currentDataKeyConfig.hintText) {
  //     sections.push(`<hint-text>`);
  //     sections.push(`${currentDataKeyConfig.hintText}`);
  //     sections.push(`</hint-text>`);
  //     sections.push("");
  //   }

  //   // Add output format if provided
  //   if (currentDataKeyConfig.outputFormat) {
  //     sections.push(`<output-format>`);
  //     sections.push(`${currentDataKeyConfig.outputFormat}`);
  //     sections.push(`</output-format>`);
  //     sections.push("");
  //   }

  //   // Add additional format instructions if provided
  //   if (currentDataKeyConfig.additionalFormatInstructions) {
  //     sections.push(`<additional-format-instructions>`);
  //     sections.push(
  //       `${JSON.stringify(currentDataKeyConfig.additionalFormatInstructions, null, 4)}`
  //     );
  //     sections.push(`</additional-format-instructions>`);
  //     sections.push("");
  //   }

  //   // Add example answer if provided
  //   if (currentDataKeyConfig.exampleAnswer) {
  //     sections.push(`<example-answer>`);
  //     sections.push(`${currentDataKeyConfig.exampleAnswer}`);
  //     sections.push(`</example-answer>`);
  //   }
  // }

  // sections.push("</data-key-config>");

  // pass in related data keys. should be list of <data-key-id> tags with values
  // if (contextConfig) {
  //   sections.push(`<related-data-keys>`);
  //   contextConfig.forEach((dataKey, dataKeyId) => {
  //     sections.push(`<${dataKeyId}>${dataKey.value}</${dataKeyId}>`);
  //   });
  //   sections.push(`</related-data-keys>`);
  // }

  if (additionalContext) {
    sections.push(`<additional-context>`);
    sections.push(`${additionalContext}`);
    sections.push(`</additional-context>`);
  }
  sections.push("</context>");

  sections.push("");
  sections.push(`<user-query>`);
  sections.push(`${instructions}`);
  sections.push(`</user-query>`);
  sections.push("");

  return sections.join("\n");
}

/**
 * Builds a data key config from a template element
 * @param dataKeyId - The data key ID
 * @param templatesConfig - The templates configuration
 * @returns A DataKeyConfig object
 */
export function buildDataKeyConfigFromElement(
  dataKeyId: string,
  templatesConfig: TemplateConfig
): DataKeyConfig {
  const element = getElementConfigByDataKey(dataKeyId, templatesConfig);

  if (!element) {
    throw new Error(`Element with data key ${dataKeyId} not found`);
  }

  return buildDataKeyConfig(element);
}

/**
 * Builds a data key config from an already-resolved template question.
 * Use this when the step is already available (e.g. rendered from a saved
 * document version) so no lookup against the current template config is needed.
 * @param element - The resolved template question
 * @returns A DataKeyConfig object
 */
export function buildDataKeyConfig(element: TemplateQuestion): DataKeyConfig {
  const dataKeyId = element.id;

  const config: DataKeyConfig = {
    dataKeyId,
    question:
      element.element.options.label ||
      `Please provide a value for ${dataKeyId}`,
  };

  // Add hint text if available
  if (element.element.options.helperText) {
    config.hintText = element.element.options.helperText;
  }

  // Determine output format based on element type
  switch (element.element.type) {
    case "table":
      config.outputFormat = "markdown table";
      if (element.element.options.columns) {
        config.additionalFormatInstructions = {
          columns: element.element.options.columns.map((col: any) => ({
            name: col.name,
            type: "string", // Default type, could be enhanced
          })),
        };
      }
      break;
    case "textField":
      config.outputFormat =
        element.element.options.rows && element.element.options.rows > 1
          ? "multi-line text"
          : "single-line text";
      break;
    case "select":
      config.outputFormat = "single selection";
      if (element.element.options.options) {
        config.additionalFormatInstructions = {
          options: element.element.options.options,
        };
      }
      break;
    default:
      config.outputFormat = "text";
  }

  // Add default value as example if available
  if (element.element.options.default) {
    config.exampleAnswer = element.element.options.default;
  }

  return config;
}

/**
 * Builds a complete prompt for auto-suggestion based on a data key
 * @param dataKeyId - The data key ID
 * @param templatesConfig - The templates configuration
 * @param customInstructions - Optional custom instructions to append
 * @returns A formatted prompt string
 */
export function buildAutoSuggestPrompt(
  dataKeyId: string,
  templatesConfig: TemplateConfig,
  contextConfig: ContextState,
  device: Device,
  customInstructions?: string
): string {
  const config = buildDataKeyConfigFromElement(dataKeyId, templatesConfig);

  // const supportingInstructions = getSupportingInstructions(dataKeyId);

  const instructions =
    customInstructions || `Create an answer for the data key ${dataKeyId}.`;

  return buildPrompt({
    instructions,
    currentDataKeyConfig: config,
    contextConfig,
    device,
    // additionalContext: supportingInstructions,
  });
}

/**
 * Builds an auto-suggest prompt from an already-resolved template question.
 * Prefer this over buildAutoSuggestPrompt when the step is already available,
 * so the data key is not re-resolved against the current template config (which
 * can throw when a saved document version references a step the template no
 * longer defines).
 * @param element - The resolved template question
 * @param contextConfig - The context state
 * @param device - The device
 * @param customInstructions - Optional custom instructions to append
 * @returns A formatted prompt string
 */
export function buildAutoSuggestPromptFromElement(
  element: TemplateQuestion,
  contextConfig: ContextState,
  device: Device,
  customInstructions?: string
): string {
  const config = buildDataKeyConfig(element);

  const instructions =
    customInstructions || `Create an answer for the data key ${element.id}.`;

  return buildPrompt({
    instructions,
    currentDataKeyConfig: config,
    contextConfig,
    device,
  });
}

// function getSupportingInstructions(
//   dataKeyId: string
// ): string | undefined {
//   const additionalPrompt = Object.keys(SUPPORTING_INSTRUCTIONS).find(
//     (key) => key === dataKeyId
//   );
//   if (!additionalPrompt) {
//     return undefined;
//   }

//   const addon =
//     SUPPORTING_INSTRUCTIONS[
//       additionalPrompt as keyof typeof SUPPORTING_INSTRUCTIONS
//     ];
//   const prompt = addon.prompt;
//   const example = (addon as any).example || "";
//   return `<supporting-instructions>${prompt}\n\nExample:${example}</supporting-instructions>`;
// }

export function buildChangeAssistantPrompt(
  dataKeyId: string,
  assistantProcess: AssistantProcess,
  templatesConfig: TemplateConfig,
  contextConfig: ContextState,
  device: Device,
  customInstructions?: string
): string {
  const config = buildDataKeyConfigFromElement(dataKeyId, templatesConfig);

  // check if we have additional prompt information in the prompt-addon.ts that is a match for the dataKeyId
  // const supportingInstructions = getSupportingInstructions(dataKeyId);

  const assistantProcessString = `<change-process-state>${JSON.stringify(
    assistantProcess,
    null,
    4
  )}</change-process-state>`;

  const instructions =
    customInstructions ||
    `Create an answer for the data key ${dataKeyId} or help me adjust if it is needed considering the change process state.`;

  return buildPrompt({
    instructions,
    currentDataKeyConfig: config,
    contextConfig,
    device,
    additionalContext: assistantProcessString,
  });
}

/**
 * Extracts data key IDs from a prompt built by buildDataKeyPrompt
 * @param prompt - The prompt string to parse
 * @returns An array of data key IDs found in the prompt
 */
export function extractDataKeysFromPrompt(prompt: string): string[] {
  const dataKeyRegex = /<data-key-id>\s*(.*?)\s*<\/data-key-id>/g;
  const dataKeys: string[] = [];
  let match;

  while ((match = dataKeyRegex.exec(prompt)) !== null) {
    const dataKeyId = match[1].trim();
    if (dataKeyId) {
      dataKeys.push(dataKeyId);
    }
  }

  return dataKeys;
}
